import React, { useState } from 'react';
import { Typography, Box } from '@mui/material';
import ActivityLayout from '../ActivityLayout';

function MatchPairs({ onBack, onCorrect, initialTimer = null, globalTimer = null, onGlobalTimeUp, score }) {
  const dominoUnicode = ['🁣', '🁤', '🁥', '🁦', '🁧', '🁨', '🁩', '🂎', '🂏', '🂐'];
  const [pairs, setPairs] = useState(generatePairs());
  const [shuffledDominoes, setShuffledDominoes] = useState(shuffleArray([...pairs.dominoes]));
  const [selectedType, setSelectedType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [emoji, setEmoji] = useState(null);
  const [showNext, setShowNext] = useState(false);
  const [currentTimer, setCurrentTimer] = useState(initialTimer);

  const numberStyle = {
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
    width: 60,
    height: 120,
    border: '2px solid black',
    borderRadius: '12px',
    backgroundColor: 'white',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  };

  const dominoStyle = {
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: 130,
    height: 180,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  };

  function generatePairs() {
    // Create array of indices 0-9 and shuffle them
    const indices = Array.from({ length: 10 }, (_, i) => i);
    const shuffledIndices = shuffleArray([...indices]);
    // Take first 5 indices to ensure unique numbers
    const numbers = shuffledIndices.slice(0, 5);
    const dominoes = numbers.map(num => dominoUnicode[num]);
    return { numbers, dominoes };
  }

  function shuffleArray(array) {
    return array.sort(() => Math.random() - 0.5);
  }

  const handleSelect = (item, type) => {
    if (showNext) return;

    if (selectedType === type) {
      // Clicking same type, just update selection
      setSelectedItem(item);
      return;
    }

    if (selectedItem === null) {
      // First selection
      setSelectedItem(item);
      setSelectedType(type);
      return;
    }

    // Check match
    let isMatch = false;
    let matchedNumber, matchedDomino;

    if (type === 'number') {
      // Current selection is a number, previous was a domino
      matchedNumber = item;
      matchedDomino = selectedItem;
      isMatch = dominoUnicode[item] === selectedItem;
    } else {
      // Current selection is a domino, previous was a number
      matchedNumber = selectedItem;
      matchedDomino = item;
      isMatch = dominoUnicode[selectedItem] === item;
    }

    if (isMatch) {
      onCorrect();
      
      const remainingNumbers = pairs.numbers.filter(num => num !== matchedNumber);
      const remainingDominoes = shuffledDominoes.filter(d => d !== matchedDomino);

      setPairs({ numbers: remainingNumbers, dominoes: remainingDominoes });
      setShuffledDominoes(shuffleArray(remainingDominoes));

      if (remainingNumbers.length === 0) {
        // Show success emoji and next button when all matches are done
        setEmoji('🎉');
        setTimeout(() => {
          setShowNext(true);
        }, 500);
      } else {
        setEmoji(null); // Clear any previous emoji for intermediate matches
      }
    } else {
      setEmoji('❌');
      setTimeout(() => {
        setEmoji(null);
      }, 1000); // Clear failure emoji after 1 second
    }

    // Reset selection
    setSelectedItem(null);
    setSelectedType(null);
  };

  const handleNext = () => {
    const newPairs = generatePairs();
    setPairs(newPairs);
    // Create new shuffled dominoes from the new pairs
    setShuffledDominoes(shuffleArray([...newPairs.dominoes]));
    setSelectedType(null);
    setSelectedItem(null);
    setEmoji(null);
    setShowNext(false);
    // Reset the timer by updating currentTimer
    setCurrentTimer(initialTimer);
  };

  return (
    <ActivityLayout 
      onBack={onBack}
      timeLimit={currentTimer}
      globalTimeLimit={globalTimer}
      onTimeUp={() => {
        setEmoji('⏰');
        setShowNext(true);
      }}
      onGlobalTimeUp={onGlobalTimeUp}
      isPaused={showNext}
      score={score}
    >
      <Box onClick={showNext ? handleNext : undefined} sx={{ cursor: showNext ? 'pointer' : 'default', userSelect: 'none' }}>
        <Typography variant="h5" align="center" gutterBottom>Associez les nombres à leurs dominos :</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              justifyContent: 'center',
              gap: 4,
              flexWrap: 'nowrap',
            }}>
              {pairs.numbers.map((num) => (
                <Box 
                  key={num}
                  onClick={() => handleSelect(num, 'number')} 
                  sx={{
                    ...numberStyle,
                    color: selectedType === 'number' && selectedItem === num ? 'primary.main' : 'inherit',
                  }}
                >
                  <Typography variant="h1" sx={{ fontSize: '5rem', lineHeight: 1 }}>{num}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              justifyContent: 'center',
              gap: 0,
              flexWrap: 'nowrap',
              py: 2
            }}>
              {shuffledDominoes.map((domino) => (
                <Box 
                  key={domino}
                  onClick={() => handleSelect(domino, 'domino')} 
                  sx={{
                    ...dominoStyle,
                    color: selectedType === 'domino' && selectedItem === domino ? 'primary.main' : 'inherit',
                  }}
                >
                  <Typography variant="h1" sx={{ fontSize: '9rem', lineHeight: 1 }}>{domino}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        {emoji && <Typography variant="h1" align="center" sx={{ mt: 3 }}>{emoji}</Typography>}
        {showNext && <Typography align="center" sx={{ mt: 2 }}>Cliquez n'importe où pour continuer</Typography>}
      </Box>
    </ActivityLayout>
  );
}

export default MatchPairs;
